import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MapPin, Linkedin, Instagram } from "lucide-react";

function XIcon({ className = "" }) {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" className={className}>
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
        </svg>
    );
}

const SOCIALS = [
    { label: "LinkedIn", href: "#", icon: <Linkedin className="w-4 h-4" />, testid: "footer-social-linkedin" },
    { label: "X", href: "#", icon: <XIcon className="w-3.5 h-3.5" />, testid: "footer-social-x" },
    { label: "Instagram", href: "#", icon: <Instagram className="w-4 h-4" />, testid: "footer-social-instagram" },
];

const groups = [
    {
        title: "AI Solutions",
        items: [
            { label: "Generative AI", to: "/ai-solutions" },
            { label: "Agentic Workflows", to: "/ai-solutions" },
            { label: "Enterprise Copilots", to: "/ai-solutions" },
            { label: "Document Intelligence", to: "/ai-solutions" },
            { label: "MLOps & Deployment", to: "/ai-solutions" },
        ],
    },
    {
        title: "Talent",
        items: [
            { label: "IT Staffing", to: "/talent-solutions" },
            { label: "Contract Staffing", to: "/talent-solutions" },
            { label: "Managed Recruiting", to: "/talent-solutions" },
            { label: "AI Candidate Matching", to: "/talent-solutions" },
            { label: "Bench Enablement", to: "/talent-solutions" },
        ],
    },
    {
        title: "Engineering",
        items: [
            { label: "SaaS Products", to: "/product-engineering" },
            { label: "Enterprise Portals", to: "/product-engineering" },
            { label: "Data Platforms", to: "/product-engineering" },
            { label: "AI Dashboards", to: "/product-engineering" },
            { label: "DevOps & Cloud", to: "/product-engineering" },
        ],
    },
    {
        title: "Company",
        items: [
            { label: "About", to: "/about" },
            { label: "Products", to: "/products" },
            { label: "Industries", to: "/industries" },
            { label: "Contact", to: "/contact" },
        ],
    },
];

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative bg-[#191919] text-[#FAFAF8] overflow-hidden"
        >
            <div className="absolute inset-0 pqx-grid-bg-dark opacity-40 pointer-events-none" />
            <div
                className="absolute -top-32 left-1/2 -translate-x-1/2 w-[820px] h-[420px] rounded-full pointer-events-none"
                style={{
                    background:
                        "radial-gradient(closest-side, rgba(255,64,64,0.18), rgba(255,64,64,0) 70%)",
                }}
            />

            <div className="pqx-container relative pt-14 pb-10">
                {/* Contact Info Strip */}
                <div className="grid sm:grid-cols-2 gap-4 pb-10 border-b border-white/10">
                    <div className="pqx-glass-dark rounded-2xl p-5 flex items-center gap-4">
                        <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FF4040]/15 text-[#FF4040] shrink-0">
                            <Mail className="w-4 h-4" />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-white/55">
                                Email
                            </p>
                            <a
                                href="mailto:contact@perceptiqx.com"
                                className="mt-1 block text-[15px] font-medium hover:text-[#FF4040]"
                                data-testid="footer-email"
                            >
                                contact@perceptiqx.com
                            </a>
                        </div>
                    </div>
                    <div className="pqx-glass-dark rounded-2xl p-5 flex items-center gap-4">
                        <span className="w-9 h-9 grid place-items-center rounded-xl bg-[#FF4040]/15 text-[#FF4040] shrink-0">
                            <MapPin className="w-4 h-4" />
                        </span>
                        <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-white/55">
                                Headquarters
                            </p>
                            <p className="mt-1 text-[15px] font-medium" data-testid="footer-address">
                                Kavuri Hills, Hyderabad, India
                            </p>
                        </div>
                    </div>
                </div>

                {/* Link groups */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-10 py-14">
                    {groups.map((g) => (
                        <div key={g.title}>
                            <p className="text-xs uppercase tracking-[0.22em] text-white/45 mb-5">
                                {g.title}
                            </p>
                            <ul className="space-y-3">
                                {g.items.map((i) => (
                                    <li key={i.label}>
                                        <Link
                                            to={i.to}
                                            data-testid={`footer-link-${i.label
                                                .toLowerCase()
                                                .replace(/[^a-z]/g, "-")}`}
                                            className="text-[14px] text-white/80 hover:text-[#FF4040] transition-colors"
                                        >
                                            {i.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Bottom bar */}
                <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between pt-8 border-t border-white/10">
                    <div className="flex items-center gap-3">
                        <img
                            src="/brand/logo-light.svg"
                            alt="Perceptiqx"
                            className="h-7 w-auto"
                        />
                        <span className="text-xs text-white/45 ml-2">
                            © {new Date().getFullYear()} Perceptiqx. All rights reserved.
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        {SOCIALS.map((s) => (
                            <a
                                key={s.label}
                                href={s.href}
                                onClick={(e) => { if (s.href === "#") e.preventDefault(); }}
                                aria-label={s.label}
                                data-testid={s.testid}
                                className="w-9 h-9 grid place-items-center rounded-full border border-white/12 text-white/60 hover:text-[#FF4040] hover:border-[#FF4040]/40 transition-colors"
                            >
                                {s.icon}
                            </a>
                        ))}
                        <span className="hidden md:block text-xs text-white/35 ml-2">
                            Built for enterprise AI.
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
